import Page from '../../../components/PageWrapper';
import { useState } from 'react';
import GTable from '../../../components/elements/GTable';
import { Customer } from '../../../models/customer/Customer';
import useModal from '../../../hooks/modalHook';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OtherActionsElement } from '../../../components/elements/GTable/GTable';
import useRoute from '../../../hooks/routesHook';
import useLayoutHook from '../../../hooks/layoutHook';
import { useQuery } from '@tanstack/react-query';
import TableFilter from '../../../components/elements/GTable/InGTableComponents/TableFilter';
import TableFilterForm from '../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import { ITableFilterItem } from '../../../resources/interfaces';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import useDownload from '../../../hooks/downloadHook';
import useCustomer from '../../../hooks/customerHook';
import { Download } from 'lucide-react';
import { ICustomerType } from '../../../resources/interfaces';
import { onRemoveFilter } from '../../../utils/miscFunctions';

export interface ICell {
  cell: { value: string | null };
  row: {
    original: {
      firstName: string, lastName: string, id: string,
      customer: { firstName: string, lastName: string, id: string },
      referrerCustomer: { firstName: string, lastName: string, id: string },
      referredAccount: { firstName: string, lastName: string, id: string },
      customerType: ICustomerType
    }
  };
  original: { firstname: string, lastname: string, id: string, customer: { firstName: string, lastName: string, id: string } };
}

const Customers = (): JSX.Element => {
  const navigate = useNavigate();
  const { screenWidth } = useLayoutHook();
  const { columns, mobileColumns, customer, loading, customersPaginationData, getDefaultFilters, getCustomers } = useCustomer();
  const [searchString, setSearchString] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
   const currentPage = Number(searchParams.get("page")) || 0;
  const [showPerPage, setShowPerPage] = useState<number>(100);
  const { ModalComponent } = useModal();
  const { showExportCustomersWithActivePoliciesModal, DownloadHookModal } = useDownload();
  const { getDashboardBase } = useRoute();
  const customerName = searchString;
  const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(getDefaultFilters());
  const stage = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'stage')?.value;
  const createTo = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.to;
  const createFrom = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.from;
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const [openFilter, setOpenFilter] = useState<boolean>(false);

  function getOtherActions() {
    return [
      {
        label: 'Export active customers',
        icon: <Download className='h-4 w-4' />,
        callback: () => showExportCustomersWithActivePoliciesModal()
      }
    ];
  }

  const handlePageChange = (page: number) => {

    const updatedParams = new URLSearchParams(searchParams);
    // updatedParams.set("type", "motor");
    updatedParams.set("page", page.toString());
    setSearchParams(updatedParams);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['getCustomers', currentPage, countryId, showPerPage, searchString, stage, createFrom, createTo],
    queryFn: () => getCustomers(currentPage, showPerPage, countryId, stage, createTo, createFrom, customerName),
    staleTime: 1000 * 10,
    placeholderData: (prev) => prev,
     gcTime: 1000 * 60 * 30 * 5,
    select: (response) => {
  
      return {
        customers: response?.data?.content?.map(
          (_customer: any) => new Customer(_customer)
        ),
        pagination: {
          per_page: response?.data?.size,
          total: response?.data?.totalElements,
          totalPages: response?.data?.totalPages,
          from: response?.data?.pageable?.offset + 1,
          to: response?.data?.totalElements > response?.data?.pageable?.offset + showPerPage
            ? response?.data?.pageable?.offset + showPerPage : response?.data?.totalElements,
        },
      };
    },
  });
  
  const pagination = data?.pagination;
  const customers = data?.customers;
  return (
    <Page title="Customers">
      <div className="bg-white dark:bg-darkBg w-full min-h-full p-5  rounded-lg shadow-md dark:shadow-darkBg-shadow">
        <div className="">
          <div className="inline-flex items-center gap-2 mb-2 dark:text-darkBg-mainText">
            <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
            <h2 className="font-semibold text-3xl">Customers</h2>
          </div>

          <p className="text-sm text-slate-400">
            View a list of all customers and their details.
          </p>
        </div>

        <div className="overflow-x-auto mt-4">
          <GTable
            columns={screenWidth <= 550 ? mobileColumns : columns}
            data={customers ?? []}
            classes="h-fit"
            onSearch={(searchString: string) => setSearchString(searchString)}
            searchPlaceholder={'Search by name...'}
            tableType={'customers'}
            paginationData={pagination}
            onPageChange={handlePageChange}
            showPerPage={showPerPage}
            onShowPerPageChange={(page: number) => setShowPerPage(page)}
            loading={isLoading}
            selectedFilters={tableFilters}
            onRemoveFilter={(filter: ITableFilterItem, index: number) => { onRemoveFilter(filter, index, tableFilters, setTableFilters) }}
            actionElement={
              getOtherActions().length > 0 && (
                <OtherActionsElement getOtherActions={getOtherActions} />
              )
            }
            showFilter={
              <TableFilter
                openFilter={openFilter}
                onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                selectedFilters={tableFilters}
                content={
                  <TableFilterForm
                    filters={tableFilters}
                    getValues={(filters: ITableFilterItem[]) => {
                      setTableFilters(filters);
                      setOpenFilter(false);
                    }}
                    onResetFilters={() => setTableFilters(getDefaultFilters())}
                  />
                }
              />
            }
          />
        </div>
      </div>
      <DownloadHookModal />
      <ModalComponent />
    </Page>
  );
};

export default Customers;
